// Listener audio engine: WebCodecs Opus decode -> AudioWorklet jitter buffer, with Ogg/Opus fallback.
import { WS_BASE, API } from "@/lib/api";

export class ListenEngine {
  constructor({ onControl, onState }) {
    this.onControl = onControl || (() => {});
    this.onState = onState || (() => {});
    this.stopped = true;
    this.mode = null;
    this.volume = 0.9;
  }

  async _pickMode() {
    // iOS Safari supports neither WebCodecs nor OGG — use AAC fallback
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    if (isIOS) return "aac";
    if (!("AudioDecoder" in window) || !("AudioWorkletNode" in window)) return "ogg";
    try {
      const s = await AudioDecoder.isConfigSupported({ codec: "opus", sampleRate: 48000, numberOfChannels: 1 });
      return s.supported ? "webcodecs" : "ogg";
    } catch (e) {
      return "ogg";
    }
  }

  async play(volume) {
    this.stopped = false;
    this.volume = volume;
    this.mode = await this._pickMode();
    if (this.mode === "aac") {
      this._startAac(volume);
      this._connectWs(true); // Keep WS open for metadata + off_air events only
      this.onState("playing", this.mode);
      return;
    }
    if (this.mode === "ogg") {
      this._startOgg(volume);
      this._connectWs(true); // Keep WS open for metadata + off_air events only
      this.onState("playing", this.mode);
      return;
    }
    this.ctx = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 48000 });
    await this.ctx.audioWorklet.addModule("/audio/player-processor.js");
    this.node = new AudioWorkletNode(this.ctx, "player-processor");
    this.gain = this.ctx.createGain();
    this.gain.gain.value = volume;
    this.analyser = this.ctx.createAnalyser();
    this.analyser.fftSize = 128;
    this.node.connect(this.gain);
    this.gain.connect(this.analyser);
    this.analyser.connect(this.ctx.destination);

    this.decoder = new AudioDecoder({
      output: (ad) => {
        const n = ad.numberOfFrames;
        const buf = new Float32Array(n);
        try { ad.copyTo(buf, { planeIndex: 0 }); } catch (e) { ad.close(); return; }
        this.node.port.postMessage({ type: "pcm", samples: buf }, [buf.buffer]);
        ad.close();
      },
      error: (e) => console.warn("decoder", e),
    });
    this.decoder.configure({ codec: "opus", sampleRate: 48000, numberOfChannels: 1 });

    await this.ctx.resume();
    this._connectWs();
    this.node.port.postMessage({ type: "play" });
    this.onState("playing", this.mode);
  }

  _connectWs(isMetaOnly = false) {
    const url = isMetaOnly ? `${WS_BASE}/api/ws/listen?mode=meta` : `${WS_BASE}/api/ws/listen`;
    const ws = new WebSocket(url);
    ws.binaryType = "arraybuffer";
    this.ws = ws;
    ws.onmessage = (ev) => {
      if (typeof ev.data === "string") {
        let m;
        try { m = JSON.parse(ev.data); } catch (e) { return; }
        this.onControl(m);
        if (m.type === "resync" && this.node) this.node.port.postMessage({ type: "flush" });
        return;
      }
      if (isMetaOnly) return;
      const dv = new DataView(ev.data);
      const seq = dv.getUint32(0);
      const opus = new Uint8Array(ev.data.slice(8));
      // Insert silence for missing sequence numbers to prevent decoder drift
      if (this._lastSeq !== undefined && seq > this._lastSeq + 1) {
        const gap = seq - this._lastSeq - 1;
        const silenceSamples = Math.min(gap, 10) * 960; // cap at 200ms silence
        const silence = new Float32Array(silenceSamples);
        if (this.node) this.node.port.postMessage({ type: "pcm", samples: silence }, [silence.buffer]);
      }
      this._lastSeq = seq;
      if (this.decoder && this.decoder.state === "configured") {
        try {
          this.decoder.decode(new EncodedAudioChunk({ type: "key", timestamp: seq * 20000, data: opus }));
        } catch (e) { /* ignore */ }
      }
    };
    ws.onclose = () => {
      if (!this.stopped) setTimeout(() => { if (!this.stopped) this._connectWs(isMetaOnly); }, 1500);
    };
    ws.onerror = () => {};
  }

  _startOgg(volume) {
    this.audio = new Audio();
    this.audio.src = `${API}/stream/main.opus`;
    this.audio.volume = volume;
    this.audio.play().catch(() => this.onState("error", this.mode));
  }

  _startAac(volume) {
    this.audio = new Audio();
    this.audio.src = `${API}/stream/main.aac`;
    this.audio.volume = volume;
    this.audio.play().catch(() => this.onState("error", this.mode));
  }

  setVolume(v) {
    this.volume = v;
    if (this.gain) this.gain.gain.value = v;
    if (this.audio) this.audio.volume = v;
  }

  async stop() {
    this.stopped = true;
    try { this.ws && this.ws.close(); } catch (e) {}
    try { this.decoder && this.decoder.state !== "closed" && this.decoder.close(); } catch (e) {}
    if (this.audio) { try { this.audio.pause(); this.audio.src = ""; } catch (e) {} this.audio = null; }
    try { this.ctx && this.ctx.state !== "closed" && (await this.ctx.close()); } catch (e) {}
    this.onState("stopped", this.mode);
  }
}
